// src/components/EditorHeader/EditorHeaderView.jsx
import { CoverSelectorButton, EmojiSelectorButton } from './components';
import { theme, ConfigProvider, Button, Flex, Typography } from 'antd';
import { DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { THUMBNAIL_ITEM_META } from '../../hooks/useThumbnail';
import { CoverIcon, EmojiIcon } from '../../icons/index';
import React, { useMemo, memo, useState } from 'react';
import config from './../../../../config.json';

const { useToken } = theme;
const { Text } = Typography;

export const EditorHeaderView = memo(({ emojiState, coverState, isEditable, fileId, workspaceId }) => {
  const [showChangeCoverButton, setShowChangeCoverButton] = useState(false);
  const { token } = useToken();

  const { showEmojiButton, showCoverButton } = useMemo(() => {
    return { showEmojiButton: !emojiState?.value, showCoverButton: !coverState?.value };
  }, [coverState?.value, emojiState?.value]);

  return (
    <ConfigProvider
      theme={{
        components: {
          Button: {
            colorText: token.colorTextSecondary,
            contentLineHeight: 0.8,
          },
        },
      }}
    >
      <Flex
        vertical
        style={{
          marginBottom: emojiState?.value && coverState?.value ? '40px' : '24px',
          position: 'relative',
          width: '100%',
        }}
      >
        {coverState?.value && (
          <>
            <div
              {...(isEditable && {
                onMouseEnter: () => setShowChangeCoverButton(true),
                onMouseLeave: () => setShowChangeCoverButton(false),
              })}
              style={{ position: 'relative', width: '100%', height: '100%', maxHeight: '168px', minHeight: '168px' }}
            >
              <div style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, overflow: 'hidden' }}>
                <img
                  src={`${config.PROXY_URL}/onedoc_backend${coverState?.value}`}
                  alt="Cover"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              {(coverState?.open || showChangeCoverButton) && isEditable && (
                <div style={{ position: 'absolute', top: '24px', left: '24px' }}>
                  <Flex gap={0} align="center" justify="center" style={{ borderRadius: 4 }}>
                    <CoverSelectorButton
                      workspaceId={workspaceId}
                      fileId={fileId}
                      imageType="cover"
                      onClick={(imageData) => {
                        if (isEditable) {
                          // imageData contains: { src, id, s3_key }
                          // Pass to your existing handleSelect which expects { type, value }
                          coverState?.handleSelect({
                            type: 'thumbnail',
                            value: imageData?.src,
                          });
                        }
                      }}
                      items={THUMBNAIL_ITEM_META.thumbnail.items}
                      open={isEditable && coverState?.open}
                      onOpenChange={() => {
                        if (isEditable) coverState?.handleClick();
                      }}
                    >
                      <Button
                        style={{
                          borderRadius: 0,
                          height: 24,
                          padding: 6,
                          width: 'unset',
                          border: 'none',
                          borderTopLeftRadius: 4,
                          borderBottomLeftRadius: 4,
                        }}
                        icon={<PictureOutlined />}
                      />
                    </CoverSelectorButton>
                    <Button
                      style={{
                        borderRadius: 0,
                        height: 24,
                        padding: 4,
                        width: 'unset',
                        border: 'none',
                        borderTopRightRadius: 4,
                        borderBottomRightRadius: 4,
                      }}
                      icon={<DeleteOutlined />}
                      onClick={coverState?.handleRemove}
                    />
                  </Flex>
                </div>
              )}
            </div>
          </>
        )}
        {emojiState?.value && (
          <div
            className="editor__emoji__container"
            style={{ ...(coverState?.value && { position: 'absolute', bottom: '-35px' }) }}
          >
            <div
              style={{
                position: 'relative',
                flex: 1,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Flex className="editor__emoji__view">
                <EmojiSelectorButton
                  showPickerAlways={true}
                  onClick={(value) => {
                    if (isEditable) emojiState?.handleSelect({ type: 'icon', value: value?.code });
                  }}
                  onClose={() => {
                    if (isEditable) emojiState?.handleRemove();
                  }}
                  open={isEditable && emojiState?.open}
                  onOpenChange={() => {
                    if (isEditable) emojiState?.handleClick();
                  }}
                >
                  <Text
                    role="button"
                    tabIndex={0}
                    style={{
                      fontSize: token.fontSizeHeading1 * 1.5,
                      lineHeight: token.lineHeightHeading1,
                      cursor: 'pointer',
                    }}
                  >
                    {emojiState?.getValue()}
                  </Text>
                </EmojiSelectorButton>
              </Flex>
              <div style={{ minWidth: '180px', maxWidth: '180px' }}></div>
            </div>
          </div>
        )}
      </Flex>
      {isEditable && (showEmojiButton || showCoverButton) && (
        <div className="editor__layout__actions">
          <div className="editor__layout__actions__inner">
            {showEmojiButton && (
              <EmojiSelectorButton
                showPickerAlways={true}
                onClick={(value) => emojiState?.handleSelect({ type: 'icon', value })}
                onClose={emojiState?.handleRemove}
                open={emojiState?.open}
                onOpenChange={emojiState?.handleClick}
              >
                <div role="button" tabIndex={0} className="layout__action">
                  <span className="layout__action-icon">
                    <EmojiIcon />
                  </span>
                  <Text className="layout__action-text">Add icon</Text>
                </div>
              </EmojiSelectorButton>
            )}
            {showCoverButton && (
              <CoverSelectorButton
                workspaceId={workspaceId}
                fileId={fileId}
                imageType="thumbnail"
                onClick={(imageData) => {
                  if (isEditable) {
                    coverState?.handleSelect({
                      type: 'thumbnail',
                      value: imageData?.src,
                    });
                  }
                }}
                items={THUMBNAIL_ITEM_META.thumbnail.items}
                open={coverState?.open}
                onOpenChange={coverState?.handleClick}
              >
                <div role="button" tabIndex={0} className="layout__action">
                  <span className="layout__action-icon">
                    <CoverIcon />
                  </span>
                  <Text className="layout__action-text">Add cover</Text>
                </div>
              </CoverSelectorButton>
            )}
          </div>
          <div style={{ minWidth: '180px', maxWidth: '180px' }}></div>
        </div>
      )}
    </ConfigProvider>
  );
});

export default EditorHeaderView;
